import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, Calendar, Lock, CheckCircle, AlertCircle } from 'lucide-react';
export function PaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState('');
  // Get service request details from location state
  const serviceRequest = location.state?.serviceRequest;
  useEffect(() => {
    // If no service request data, redirect back to request service page
    if (!serviceRequest && !isComplete) {
      navigate('/customer/request-service');
    }
  }, [serviceRequest, navigate, isComplete]);
  const formatCardNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    // Add space after every 4 digits
    const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    // Limit to 19 characters (16 digits + 3 spaces)
    return formatted.slice(0, 19);
  };
  const formatExpiry = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    // Format as MM/YY
    if (digits.length > 2) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
    }
    return digits;
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
  };
  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiry(formatExpiry(e.target.value));
  };
  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow up to 4 digits for CVV
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setCvv(value);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Basic validation
    if (!cardNumber.trim() || !cardName.trim() || !expiry.trim() || !cvv.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    // Simulate payment processing
    setIsProcessing(true);
    // Simulate API call with timeout
    setTimeout(() => {
      setIsProcessing(false);
      setIsComplete(true);
      // In a real app, you would save the order to your backend
      // For now, just store in localStorage for demo purposes
      const orders = JSON.parse(localStorage.getItem('customerOrders') || '[]');
      const newOrder = {
        id: Date.now().toString(),
        service: serviceRequest?.serviceType === 'painting' ? 'Painting Service' : serviceRequest?.serviceType === 'waterproofing' ? 'Waterproofing Service' : 'Micro-repair Service',
        date: new Date().toISOString(),
        status: 'pending',
        total: serviceRequest?.serviceType === 'painting' ? 450 : 750,
        paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'Online Banking'
      };
      orders.push(newOrder);
      localStorage.setItem('customerOrders', JSON.stringify(orders));
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/customer/dashboard');
      }, 2000);
    }, 2000);
  };
  if (isComplete) {
    return <div className="max-w-md mx-auto bg-white rounded-lg shadow p-8 text-center" data-id="element-1025">
        <div className="mb-6 flex justify-center" data-id="element-1026">
          <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center" data-id="element-1027">
            <CheckCircle className="h-8 w-8 text-green-600" data-id="element-1028" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1029">
          Payment Successful!
        </h2>
        <p className="text-gray-600 mb-6" data-id="element-1030">
          Your service request has been confirmed. You will receive an email
          with the details shortly.
        </p>
        <p className="text-gray-500 text-sm" data-id="element-1031">Redirecting to dashboard...</p>
      </div>;
  }
  return <div className="light" data-id="element-1032">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1033">Payment Details</h1>
      {serviceRequest && <div className="bg-primary/5 rounded-lg p-4 mb-6" data-id="element-1034">
          <h2 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1035">
            Service Summary
          </h2>
          <div className="flex justify-between mb-1" data-id="element-1036">
            <span className="text-gray-600" data-id="element-1037">Service Type:</span>
            <span className="font-medium" data-id="element-1038">
              {serviceRequest.serviceType === 'painting' && 'Painting Service'}
              {serviceRequest.serviceType === 'waterproofing' && 'Waterproofing Service'}
              {serviceRequest.serviceType === 'microrepair' && 'Micro-repair Service'}
            </span>
          </div>
          <div className="flex justify-between mb-1" data-id="element-1039">
            <span className="text-gray-600" data-id="element-1040">Total Amount:</span>
            <span className="font-medium" data-id="element-1041">
              MYR{' '}
              {serviceRequest.serviceType === 'painting' ? '450.00' : '750.00'}
            </span>
          </div>
          {serviceRequest.paymentMethod === 'installment' && <div className="flex justify-between" data-id="element-1042">
              <span className="text-gray-600" data-id="element-1043">Payment Plan:</span>
              <span className="font-medium" data-id="element-1044">3-month 0% installment</span>
            </div>}
        </div>}
      <div className="bg-white rounded-lg shadow p-6" data-id="element-1045">
        <div className="mb-6" data-id="element-1046">
          <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1047">
            Select Payment Method
          </h2>
          <div className="flex space-x-4" data-id="element-1048">
            <label className="flex-1 border rounded-lg p-4 cursor-pointer transition-colors hover:border-primary hover:bg-primary/5" data-id="element-1049">
              <input type="radio" name="paymentMethod" value="credit" checked={paymentMethod === 'credit'} onChange={() => setPaymentMethod('credit')} className="sr-only" data-id="element-1050" />
              <div className={`flex items-center ${paymentMethod === 'credit' ? 'text-primary' : 'text-gray-700'}`} data-id="element-1051">
                <CreditCard className="h-5 w-5 mr-2" data-id="element-1052" />
                <span data-id="element-1053">Credit / Debit Card</span>
              </div>
            </label>
            <label className="flex-1 border rounded-lg p-4 cursor-pointer transition-colors hover:border-primary hover:bg-primary/5" data-id="element-1054">
              <input type="radio" name="paymentMethod" value="banking" checked={paymentMethod === 'banking'} onChange={() => setPaymentMethod('banking')} className="sr-only" data-id="element-1055" />
              <div className={`flex items-center ${paymentMethod === 'banking' ? 'text-primary' : 'text-gray-700'}`} data-id="element-1056">
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1057">
                  <path d="M2 8H22M6 8V18M18 8V18M4 8H20C21.1046 8 22 7.10457 22 6V4C22 2.89543 21.1046 2 20 2H4C2.89543 2 2 2.89543 2 4V6C2 7.10457 2.89543 8 4 8ZM3 18H21C21.5523 18 22 18.4477 22 19V20C22 21.1046 21.1046 22 20 22H4C2.89543 22 2 21.1046 2 20V19C2 18.4477 2.44772 18 3 18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1058" />
                </svg>
                <span data-id="element-1059">Online Banking</span>
              </div>
            </label>
          </div>
        </div>
        {paymentMethod === 'credit' && <form onSubmit={handleSubmit} data-id="element-1060">
            <div className="space-y-4" data-id="element-1061">
              <div data-id="element-1062">
                <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1063">
                  Card Number
                </label>
                <div className="relative" data-id="element-1064">
                  <input id="cardNumber" type="text" placeholder="1234 5678 9012 3456" className="pl-10 w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" value={cardNumber} onChange={handleCardNumberChange} maxLength={19} required data-id="element-1065" />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1066">
                    <CreditCard className="h-5 w-5 text-gray-400" data-id="element-1067" />
                  </div>
                </div>
              </div>
              <div data-id="element-1068">
                <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1069">
                  Cardholder Name
                </label>
                <input id="cardName" type="text" placeholder="John Smith" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" value={cardName} onChange={e => setCardName(e.target.value)} required data-id="element-1070" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1071">
                <div data-id="element-1072">
                  <label htmlFor="expiry" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1073">
                    Expiry Date
                  </label>
                  <div className="relative" data-id="element-1074">
                    <input id="expiry" type="text" placeholder="MM/YY" className="pl-10 w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" value={expiry} onChange={handleExpiryChange} maxLength={5} required data-id="element-1075" />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1076">
                      <Calendar className="h-5 w-5 text-gray-400" data-id="element-1077" />
                    </div>
                  </div>
                </div>
                <div data-id="element-1078">
                  <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1079">
                    CVV
                  </label>
                  <div className="relative" data-id="element-1080">
                    <input id="cvv" type="text" placeholder="123" className="pl-10 w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" value={cvv} onChange={handleCvvChange} maxLength={4} required data-id="element-1081" />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1082">
                      <Lock className="h-5 w-5 text-gray-400" data-id="element-1083" />
                    </div>
                  </div>
                </div>
              </div>
              {error && <div className="flex items-center text-red-600 text-sm" data-id="element-1084">
                  <AlertCircle className="h-4 w-4 mr-2" data-id="element-1085" />
                  {error}
                </div>}
              <div className="flex items-center text-sm text-gray-500 mt-4" data-id="element-1086">
                <Lock className="h-4 w-4 mr-2" data-id="element-1087" />
                Your payment information is secure and encrypted
              </div>
            </div>
            <div className="mt-8" data-id="element-1088">
              <button type="submit" disabled={isProcessing} className="w-full px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-70 flex items-center justify-center" data-id="element-1089">
                {isProcessing ? <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1090">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1091"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1092"></path>
                    </svg>
                    Processing...
                  </> : 'Complete Payment'}
              </button>
            </div>
          </form>}
        {paymentMethod === 'banking' && <form onSubmit={handleSubmit} data-id="element-1093">
            <div className="space-y-4" data-id="element-1094">
              <div data-id="element-1095">
                <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1096">
                  Select Bank
                </label>
                <select id="bankName" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" required data-id="element-1097">
                  <option value="" data-id="element-1098">Select your bank</option>
                  <option value="maybank" data-id="element-1099">Maybank</option>
                  <option value="cimb" data-id="element-1100">CIMB Bank</option>
                  <option value="publicbank" data-id="element-1101">Public Bank</option>
                  <option value="rhb" data-id="element-1102">RHB Bank</option>
                  <option value="hongleong" data-id="element-1103">Hong Leong Bank</option>
                  <option value="ambank" data-id="element-1104">AmBank</option>
                </select>
              </div>
              {error && <div className="flex items-center text-red-600 text-sm" data-id="element-1105">
                  <AlertCircle className="h-4 w-4 mr-2" data-id="element-1106" />
                  {error}
                </div>}
              <div className="flex items-center text-sm text-gray-500 mt-4" data-id="element-1107">
                <Lock className="h-4 w-4 mr-2" data-id="element-1108" />
                You will be redirected to your bank's secure payment page
              </div>
            </div>
            <div className="mt-8" data-id="element-1109">
              <button type="submit" disabled={isProcessing} className="w-full px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-70 flex items-center justify-center" data-id="element-1110">
                {isProcessing ? <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1111">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1112"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1113"></path>
                    </svg>
                    Processing...
                  </> : 'Proceed to Bank'}
              </button>
            </div>
          </form>}
      </div>
    </div>;
}